import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip, Alert, CircularProgress,
  IconButton, Collapse, Switch, FormControlLabel, Tooltip,
} from '@mui/material';
import {
  InfoOutlined, KeyboardArrowDown as ExpandIcon, KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import {
  getTaxDeposits, updateTaxDepositSettings, recordTaxDeposit, voidTaxDeposit,
} from '../api';
import { useFeatures } from '../context/FeaturesContext';
import { isPayrollReadOnly, PAYROLL_READ_ONLY_TEXT } from '../utils/featureFlags';

// The server computes every due date and status (plan 117); this screen only renders them.
const AGENCY_OPTIONS = [
  ['federal_941', 'Federal (Form 941)'], ['futa', 'FUTA (Form 940)'], ['pa_withholding', 'PA withholding'],
  ['pa_uc', 'PA unemployment (UC)'], ['local', 'Local EIT/LST'],
];
const PA_LABELS = { quarterly: 'Quarterly', monthly: 'Monthly', semimonthly: 'Semi-monthly', semiweekly: 'Semi-weekly' };
const DETAIL_FIELDS = {
  federal_941: [['federal_income_tax', 'Federal income tax'], ['ss_employee', 'Social Security (employee)'],
    ['ss_employer', 'Social Security (employer)'], ['medicare_employee', 'Medicare (employee)'], ['medicare_employer', 'Medicare (employer)']],
  futa: [['taxable_wages', 'Taxable wages'], ['tax', 'Tax']],
  pa_withholding: [['pa_wages', 'PA wages'], ['tax', 'Tax']],
  pa_uc: [['uc_gross_wages', 'UC gross wages'], ['employee', 'Employee'], ['employer', 'Employer']],
  local: [['eit_wages', 'EIT wages'], ['eit', 'EIT'], ['lst', 'LST']],
};
const STATUS_CHIPS = {
  due: { label: 'Due', color: 'info' },
  overdue: { label: 'Overdue', color: 'error' },
  partially_paid: { label: 'Partially paid', color: 'warning' },
  paid: { label: 'Paid', color: 'success' },
};

const fmtDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
};
const fmtMoney = (x) => `$${Number(x || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const periodText = (o) => `${fmtDate(o.period_start)} - ${fmtDate(o.period_end)}${o.kind === 'next_day' ? ' (next-day)' : ''}`;

function Info({ text }) {
  return (
    <Tooltip title={text} arrow>
      <InfoOutlined fontSize="small" color="action" />
    </Tooltip>
  );
}

function PaymentsTable({ payments, onVoid }) {
  if (!payments || payments.length === 0) return <Typography variant="caption" color="text.secondary">No payments recorded.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Paid on</TableCell><TableCell align="right">Amount</TableCell><TableCell>Confirmation #</TableCell>
          <TableCell>Recorded by</TableCell><TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {payments.map((p) => (
          <TableRow key={p.id}>
            <TableCell sx={p.voided_at ? { textDecoration: 'line-through' } : undefined}>{fmtDate(p.paid_on)}</TableCell>
            <TableCell align="right" sx={p.voided_at ? { textDecoration: 'line-through' } : undefined}>{fmtMoney(p.amount)}</TableCell>
            <TableCell sx={p.voided_at ? { textDecoration: 'line-through' } : undefined}>{p.confirmation_number || ''}</TableCell>
            <TableCell>
              {p.recorded_by || ''}
              {p.voided_at && <Typography variant="caption" display="block" color="text.secondary">Voided: {p.void_reason}</Typography>}
            </TableCell>
            <TableCell>
              {!p.voided_at && onVoid && <Button size="small" color="error" onClick={() => onVoid(p)}>Void</Button>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ObligationRow({ o, onRecord, onVoid }) {
  const [open, setOpen] = useState(false);
  const chip = STATUS_CHIPS[o.status] || STATUS_CHIPS.due;
  const fields = DETAIL_FIELDS[o.agency] || [];
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Hide details' : 'Show details'} onClick={() => setOpen(!open)}>
            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          {fmtDate(o.due_date)}
          {o.from_prior_year && <Typography variant="caption" display="block" color="text.secondary">from {o.tax_year}</Typography>}
        </TableCell>
        <TableCell>
          {o.agency_label}
          <Typography variant="caption" display="block" color="text.secondary">{o.pay_where}</Typography>
        </TableCell>
        <TableCell>{periodText(o)}</TableCell>
        <TableCell align="right">{fmtMoney(o.amount)}</TableCell>
        <TableCell align="right">{fmtMoney(o.paid)}</TableCell>
        <TableCell align="right">{fmtMoney(o.balance)}</TableCell>
        <TableCell>
          <Chip size="small" color={chip.color} label={chip.label + (o.status === 'partially_paid' && o.past_due ? ' - past due' : '')} />
        </TableCell>
        <TableCell>
          {Number(o.balance) > 0 && <Button size="small" variant="outlined" onClick={() => onRecord(o)}>Record payment</Button>}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">{o.due_rule}</Typography>
              {(o.notes || []).map((n) => (
                <Typography key={n} variant="caption" color="warning.main" display="block">{n}</Typography>
              ))}
              <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Liability per payday</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Paycheck date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    {fields.map(([k, label]) => <TableCell key={k} align="right">{label}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(o.check_dates || []).map((c) => (
                    <TableRow key={c.check_date}>
                      <TableCell>{fmtDate(c.check_date)}</TableCell>
                      <TableCell align="right">{fmtMoney(c.amount)}</TableCell>
                      {fields.map(([k]) => <TableCell key={k} align="right">{fmtMoney((c.detail || {})[k])}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Payments</Typography>
              <PaymentsTable payments={o.payments} onVoid={onVoid} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function TaxDeposits() {
  const { features } = useFeatures();
  const readOnly = isPayrollReadOnly(features);
  const [year, setYear] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [agencyFilter, setAgencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ federal_deposit_schedule: '', pa_withholding_frequency: '', reminder_enabled: false, reminder_days: 5 });
  const [recordFor, setRecordFor] = useState(null);
  const [pay, setPay] = useState({ paid_on: '', amount: '', confirmation_number: '', note: '' });
  const [voidFor, setVoidFor] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (y) => {
    setLoading(true);
    setError('');
    try {
      const res = await getTaxDeposits(y ? { year: y } : {});
      setData(res.data);
      setYear(res.data.year);
      const s = res.data.settings;
      setForm({
        federal_deposit_schedule: s.federal_deposit_schedule || '', pa_withholding_frequency: s.pa_withholding_frequency || '',
        reminder_enabled: !!s.reminder_enabled, reminder_days: s.reminder_days,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tax deposits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      await updateTaxDepositSettings({
        federal_deposit_schedule: form.federal_deposit_schedule || null,
        pa_withholding_frequency: form.pa_withholding_frequency || null,
        reminder_enabled: form.reminder_enabled,
        reminder_days: Number(form.reminder_days),
      });
      setMessage('Deposit settings saved');
      await load(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save deposit settings');
    } finally {
      setSaving(false);
    }
  };

  const openRecord = (o) => {
    setRecordFor(o);
    setPay({ paid_on: data.as_of, amount: o.balance, confirmation_number: '', note: '' });
    setError('');
  };
  const submitRecord = async () => {
    setSaving(true);
    setError('');
    try {
      await recordTaxDeposit({
        agency: recordFor.agency, collector_key: recordFor.collector_key, period_start: recordFor.period_start,
        period_end: recordFor.period_end, amount: pay.amount, paid_on: pay.paid_on,
        confirmation_number: pay.confirmation_number, note: pay.note,
      });
      setRecordFor(null);
      setMessage('Payment recorded');
      await load(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };
  const submitVoid = async () => {
    setSaving(true);
    setError('');
    try {
      await voidTaxDeposit(voidFor.id, { reason });
      setVoidFor(null);
      setReason('');
      setMessage('Payment voided');
      await load(year);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to void payment');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !data) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  const obligations = (data ? data.obligations : []).filter((o) => (!agencyFilter || o.agency === agencyFilter)
    && (!statusFilter || (statusFilter === 'paid' ? o.status === 'paid' : o.status !== 'paid')));
  const summary = data ? data.quarter_summary : [];
  const showPrior = summary.some((q) => Number(q.prior_provider) > 0);
  const lookback = data && data.federal.lookback;

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold">Tax Deposits</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What is due, to whom and when. HourTrax does not pay anything: make each deposit on the agency's site, then click Record payment.
      </Typography>
      {readOnly && <Alert severity="info" sx={{ mb: 2, py: 0 }}>{PAYROLL_READ_ONLY_TEXT}</Alert>}
      {error && !recordFor && !voidFor && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      {data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Deposit settings</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField select size="small" label="Federal deposit schedule" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 200 }}
                value={form.federal_deposit_schedule} onChange={(e) => setForm({ ...form, federal_deposit_schedule: e.target.value })}>
                <MenuItem value="">Not set (monthly)</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="semiweekly">Semiweekly</MenuItem>
              </TextField>
              <Info text="From your IRS lookback period (July 1 to June 30 of the year before last): $50,000 or less of Form 941 taxes is Monthly, more is Semiweekly. A new employer is Monthly for its first calendar year. Your previous payroll provider can tell you yours. A $100,000 day switches you to Semiweekly automatically." />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField select size="small" label="PA withholding frequency" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 200 }}
                value={form.pa_withholding_frequency} onChange={(e) => setForm({ ...form, pa_withholding_frequency: e.target.value })}>
                <MenuItem value="">Not set</MenuItem>
                {Object.entries(PA_LABELS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
              </TextField>
              <Info text="The frequency PA Department of Revenue assigned you (see myPATH). REV-415 thresholds: under $300 a quarter is Quarterly; $300 to $999 Monthly; $1,000 to $4,999 Semi-monthly; $5,000 or more a quarter (or $20,000 a year) Semi-weekly." />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControlLabel label="Email reminders"
                control={<Switch checked={form.reminder_enabled} onChange={(e) => setForm({ ...form, reminder_enabled: e.target.checked })} />} />
              <Info text="Emails and notifies everyone who can open Tax Deposits (Admin, Payroll Admin, and roles with both Access tax forms and Run payroll) once per deposit, the chosen number of days before it is due, while it is not fully paid. Reminders go only to Admin, Payroll Admin, and roles with both Access tax forms and Run payroll. If the person who pays your deposits has Run payroll but not Access tax forms, add Access tax forms to their role in Settings → Roles." />
            </Box>
            <TextField type="number" size="small" label="Days before due date" InputLabelProps={{ shrink: true }} sx={{ width: 160 }}
              inputProps={{ min: 1, max: 30 }} disabled={!form.reminder_enabled}
              value={form.reminder_days} onChange={(e) => setForm({ ...form, reminder_days: e.target.value })} />
            <Button size="small" variant="contained" onClick={saveSettings} disabled={saving}>Save settings</Button>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {lookback && lookback.complete
              ? `HourTrax lookback estimate: ${lookback.schedule === 'semiweekly' ? 'Semiweekly' : 'Monthly'} ($${lookback.total} for ${fmtDate(lookback.from)} to ${fmtDate(lookback.to)})`
              : 'HourTrax does not have a full lookback period of its own payroll yet.'}
            {data.pa_withholding.frequency_advisory
              ? ` HourTrax estimate from REV-415 thresholds: ${PA_LABELS[data.pa_withholding.frequency_advisory]}` : ''}
          </Typography>
        </Paper>
      )}

      {data && data.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}

      {data && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', my: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField select size="small" label="Year" InputLabelProps={{ shrink: true }} sx={{ minWidth: 110 }}
              value={year || ''} onChange={(e) => load(e.target.value)}>
              {data.supported_years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <Info text="The tax year whose deposits are listed. December deposits due in January appear under the new year, marked from the prior year." />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField select size="small" label="Agency" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 190 }}
              value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)}>
              <MenuItem value="">All agencies</MenuItem>
              {AGENCY_OPTIONS.map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
            </TextField>
            <Info text="Show only the deposits for one agency." />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField select size="small" label="Status" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ minWidth: 130 }}
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
            </TextField>
            <Info text="Open means due, overdue or partially paid." />
          </Box>
        </Box>
      )}

      {data && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Deposits</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Due date</TableCell><TableCell>Agency</TableCell><TableCell>Period</TableCell>
                  <TableCell align="right">Amount due</TableCell><TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Balance</TableCell><TableCell>Status</TableCell><TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {obligations.map((o) => (
                  <ObligationRow key={`${o.key}|${o.due_date}`} o={o} onRecord={openRecord}
                    onVoid={(p) => { setVoidFor(p); setReason(''); }} />
                ))}
                {obligations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary">
                        No deposits for {year}. Deposits appear once a payroll run with a Paycheck Date in {year} is processed.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="subtitle2">Form 941: taxes vs deposits</Typography>
              <Info text="Line 12 is the quarter's Form 941 tax from processed payroll by Paycheck Date. Line 13 is the federal deposits recorded here for that quarter's paydays. Record your deposits, then regenerate the quarter's Form 941 on Tax Filing so its lines 13-15 match." />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Quarter</TableCell>
                  <TableCell align="right">Total taxes (line 12)</TableCell>
                  {showPrior && <TableCell align="right">Deposited by prior provider (YTD import)</TableCell>}
                  <TableCell align="right">Deposits recorded (line 13)</TableCell>
                  <TableCell align="right">Difference</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((q) => (
                  <TableRow key={q.quarter}>
                    <TableCell>Q{q.quarter}</TableCell>
                    <TableCell align="right">{fmtMoney(q.line12)}</TableCell>
                    {showPrior && <TableCell align="right">{fmtMoney(q.prior_provider)}</TableCell>}
                    <TableCell align="right">{fmtMoney(q.line13)}</TableCell>
                    <TableCell align="right" sx={Number(q.unexplained) !== 0 ? { color: 'warning.main', fontWeight: 'bold' } : undefined}>
                      {fmtMoney(q.unexplained)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {data.unmatched_payments.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2">Payments not matching a current deposit</Typography>
                <Info text="These payments' period no longer matches a computed deposit, for example after the deposit schedule changed. Void and re-record them against the right row." />
              </Box>
              <PaymentsTable payments={data.unmatched_payments} onVoid={(p) => { setVoidFor(p); setReason(''); }} />
            </Paper>
          )}
        </>
      )}

      <Dialog open={!!recordFor} onClose={() => setRecordFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Record payment
          {recordFor && (
            <Typography variant="body2" color="text.secondary">
              {recordFor.agency_label}, {periodText(recordFor)}, due {fmtDate(recordFor.due_date)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField fullWidth size="small" type="date" label="Paid on" InputLabelProps={{ shrink: true }}
                value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} />
              <Info text="The date the payment settles. You can enter a date up to 30 days ahead if you scheduled it in EFTPS." />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField fullWidth size="small" label="Amount" InputLabelProps={{ shrink: true }}
                value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
              <Info text="The amount you paid for this deposit. Partial payments are fine; record each one." />
            </Box>
            {recordFor && Number(pay.amount) > Number(recordFor.balance) && (
              <Alert severity="info">This is more than the balance; the extra shows as overpaid.</Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField fullWidth size="small" label="Confirmation #" InputLabelProps={{ shrink: true }}
                value={pay.confirmation_number} onChange={(e) => setPay({ ...pay, confirmation_number: e.target.value })} />
              <Info text="EFTPS EFT acknowledgement number, myPATH confirmation number, UC system or collector confirmation." />
            </Box>
            <TextField fullWidth size="small" label="Note" InputLabelProps={{ shrink: true }}
              value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordFor(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitRecord} disabled={saving}>Record payment</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!voidFor} onClose={() => setVoidFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Void payment</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <TextField fullWidth multiline minRows={2} size="small" label="Reason" required sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }} value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidFor(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitVoid} disabled={saving || !reason.trim()}>Void payment</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
